import { useAuth0 } from '@auth0/auth0-react';
import { ANALYTICS_EVENTS } from '@syncanix/shared';
import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

import { env } from '@/config/env';
import { useT } from '@/core/providers/I18nProvider';
import { useWorkspace } from '@/core/providers/WorkspaceProvider';

import { analytics, initAnalytics } from './posthog-client';

import type { PropsWithChildren, ReactElement } from 'react';

/**
 * Side-effect provider that wires PostHog to the dashboard's existing context.
 * Renders its children unchanged — it exists purely for its effects. Must mount
 * INSIDE BrowserRouter (useLocation), Auth0Provider (useAuth0), I18nProvider
 * (useT), and WorkspaceProvider (useWorkspace) — see Providers.tsx ordering.
 *
 * When `VITE_POSTHOG_KEY` is unset (local dev), `initAnalytics` returns false
 * and every `analytics.*` call below is a no-op, so the wiring is inert.
 */
export function AnalyticsProvider({ children }: PropsWithChildren): ReactElement {
  const location = useLocation();
  const { isAuthenticated, user } = useAuth0();
  const { currentWorkspace } = useWorkspace();
  const { locale } = useT();

  // Initialise exactly once, before the first pageview effect runs. We suppress
  // analytics on localhost (covers `pnpm dev`, `vite preview`, and Playwright
  // e2e — all served from localhost) so developer + test traffic never reaches
  // the shared PostHog project even if a key is ever present in a local build.
  // Local dev ships no key (hydrate doesn't emit it); only deployed origins do.
  const ready = useRef(false);
  if (!ready.current) {
    const isLocalhost =
      typeof window !== 'undefined' &&
      /^(localhost|127\.0\.0\.1|\[::1\])$/.test(window.location.hostname);
    initAnalytics({
      apiKey: isLocalhost ? undefined : env.VITE_POSTHOG_KEY,
      apiHost: env.VITE_POSTHOG_HOST,
      environment: import.meta.env.PROD ? 'production' : 'development',
      locale,
    });
    ready.current = true;
  }

  // Pageview on every route change (manual — autocapture is off).
  useEffect(() => {
    analytics.capturePageview(location.pathname);
  }, [location.pathname]);

  // Identify on sign-in (pseudonymous: Auth0 `sub`, no raw email); reset on sign-out.
  // The first identify for a session is also the `user_signed_in` funnel anchor.
  const lastIdentified = useRef<string | null>(null);
  useEffect(() => {
    if (isAuthenticated && user?.sub) {
      if (lastIdentified.current !== user.sub) {
        analytics.identify(user.sub, { locale });
        analytics.capture(ANALYTICS_EVENTS.USER_SIGNED_IN);
        lastIdentified.current = user.sub;
      }
    } else if (!isAuthenticated && lastIdentified.current) {
      analytics.reset();
      lastIdentified.current = null;
    }
  }, [isAuthenticated, user?.sub, locale]);

  // Associate events with the active workspace group (B2B group analytics), and
  // emit a discrete `workspace_switched` when the active workspace actually
  // changes (not on the first bind, which is just the initial selection).
  // Auth-gated: the WorkspaceProvider restores a persisted workspace from
  // localStorage on boot, so without the gate `$groupidentify` fired on
  // /signin against an anonymous distinct_id (first live PostHog data,
  // 2026-06-10) — binding a workspace group to a person that doesn't exist.
  const lastWorkspaceId = useRef<string | null>(null);
  useEffect(() => {
    if (!isAuthenticated || !currentWorkspace) {
      // Forget the pre-sign-out selection so a different user signing in
      // later in the same tab gets a clean first bind, not a phantom
      // `workspace_switched`.
      lastWorkspaceId.current = null;
      return;
    }
    analytics.groupWorkspace(currentWorkspace.id, {
      name: currentWorkspace.name,
      is_personal: currentWorkspace.isPersonal,
    });
    const previous = lastWorkspaceId.current;
    if (previous && previous !== currentWorkspace.id) {
      analytics.capture(ANALYTICS_EVENTS.WORKSPACE_SWITCHED, {
        to_workspace_id: currentWorkspace.id,
        from_workspace_id: previous,
      });
    }
    lastWorkspaceId.current = currentWorkspace.id;
  }, [isAuthenticated, currentWorkspace]);

  // Keep the locale super-property current after a language switch.
  useEffect(() => {
    analytics.setLocale(locale);
  }, [locale]);

  return <>{children}</>;
}
